import React from 'react'
import {useTranslation} from 'react-i18next'
import {RouteComponentProps} from 'react-router'
import {Redirect, Route, Switch} from 'react-router-dom'

import {Navigator} from '@progyny/components/Navigator'
import {
  SetupProfileMutationVariables,
  SetupMenopauseProfileMutationVariables,
  SetupPregnancyPostpartumProfileMutationVariables,
} from '@progyny/generated/graphql'
import useOnboardingMutations from '@progyny/hooks/useOnboardingMutations'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useProgressColor} from '@progyny/hooks/useProgressColor'
import CommonContainer from '@progyny/pages/CreateAccount/CommonContainer'
import Intro from '@progyny/pages/MigrateAccount/Intro'
import useSetupProfileRoutes from '@progyny/pages/SetUpProfile/hooks/useSetupProfileRoutes'
import {SetupProfileRoutes} from '@progyny/pages/SetUpProfile/types'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

const MigrateAccount: React.FC<RouteComponentProps> = ({
  match,
  history,
  ...props
}) => {
  const {t} = useTranslation()
  const {routes: subRoutes, navigateTo} = useSetupProfileRoutes()
  const isAccountCreated = useMeStore((s) => s.isAccountCreated)
  const isAuthorized = useMeStore((s) => s.isAuthorized)
  const isEmailConfirmed = useMeStore((s) => s.isEmailConfirmed)
  const isLoggedIn = useMeStore((s) => s.isLoggedIn)
  const isSsoLogin = useMeStore((s) => s.isSsoLogin)
  const onboardingData = useMeStore((s) => s.onboardingData)
  const user = useMeStore((s) => s.user)
  const {getPath} = useRoutes()
  const {productLine} = useProductLine()
  const progressColor = useProgressColor()
  const {
    updateLoading,
    persistSetupInformation,
    persistMenopauseProfileInformation,
    persistPregnancyPostpartumProfileInformation,
  } = useOnboardingMutations()

  // Create a wrapper function that can handle all mutation types
  const handlePersistMemberData = (
    variables:
      | SetupProfileMutationVariables
      | SetupMenopauseProfileMutationVariables
      | SetupPregnancyPostpartumProfileMutationVariables,
    onSuccess: React.Dispatch<void>
  ) => {
    if (productLine === 'menopause') {
      persistMenopauseProfileInformation(
        variables as SetupMenopauseProfileMutationVariables,
        onSuccess
      )
    } else if (productLine === 'pregnancy_and_postpartum') {
      persistPregnancyPostpartumProfileInformation(
        variables as SetupPregnancyPostpartumProfileMutationVariables,
        onSuccess
      )
    } else {
      persistSetupInformation(
        variables as SetupProfileMutationVariables,
        onSuccess
      )
    }
  }

  function getRedirectPath() {
    if (isAuthorized) {
      return getPath(RouteEnum.HOME)
    } else if (isAccountCreated) {
      return getPath(RouteEnum.SETUP_PROFILE)
    } else if (isEmailConfirmed || isSsoLogin) {
      return `${match.url}`
    } else if (isLoggedIn) {
      return {
        pathname: getPath(RouteEnum.CONFIRM_EMAIL),
        state: {email: user?.email},
      }
    } else {
      return `${getPath(RouteEnum.LOGIN)}?next=${encodeURI(match.url)}`
    }
  }

  return (
    <Switch>
      <Route {...props} exact component={Intro} path={match.url} />
      {subRoutes.map((route, idx) => {
        const RouteComponent = route.component
        return (
          <Route
            key={idx}
            path={`${match.url}${route.path}`}
            render={() => (
              <CommonContainer
                progressColor={progressColor}
                progressBarTitle={
                  route.showProgressBar !== false
                    ? productLine === 'menopause'
                      ? t('setup_profile_your_health_profile')
                      : t('setup_profile_your_family_plan')
                    : undefined
                }
                headerTitle={route?.headerTitle}
                description={route?.description}
                progressValue={((idx + 1) / subRoutes.length) * 100}
              >
                <RouteComponent
                  navigator={Navigator}
                  onboardingData={onboardingData}
                  callback={() => {
                    history.push(`${match.url}${subRoutes[idx + 1].path}`)
                  }}
                  updateLoading={updateLoading}
                  persistMemberData={handlePersistMemberData}
                  goBack={() => {
                    if (idx - 1 < SetupProfileRoutes.BIRTH_ASSIGNED_SEX) {
                      return
                    } else {
                      history.push(`${match.url}${subRoutes[idx - 1].path}`)
                    }
                  }}
                  match={match}
                  history={history}
                  navigateTo={navigateTo}
                />
              </CommonContainer>
            )}
          />
        )
      })}
      <Redirect path={`${match.url}/*`} to={getRedirectPath()} />
    </Switch>
  )
}

export default MigrateAccount
